// Keeps NIKANEKO's private _preorder line-item property in sync with the selected variant.
// The server-rendered add-to-cart button remains the source of truth for preorder eligibility.

const PREORDER_PROPERTY_SELECTOR = 'input[name="properties[_preorder]"]';
const PRODUCT_FORM_SELECTOR = 'product-form-component';
const PENDING_ATTRIBUTE = 'preorderVariantPending';

/**
 * Find the product form associated with a variant event.
 * @param {Event & {detail?: any}} event
 * @returns {HTMLElement | null}
 */
function getProductFormForEvent(event) {
  const source = event.target instanceof Element ? event.target : null;
  const scope = source?.closest('.shopify-section, dialog, product-card');
  if (!scope) return null;

  const productId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId;
  if (productId) {
    const exactForm = scope.querySelector(`${PRODUCT_FORM_SELECTOR}[data-product-id="${productId}"]`);
    if (exactForm instanceof HTMLElement) return exactForm;
  }

  const fallbackForm = scope.querySelector(PRODUCT_FORM_SELECTOR);
  return fallbackForm instanceof HTMLElement ? fallbackForm : null;
}

/**
 * Find the matching product form in the newly-rendered section HTML.
 * @param {Document | Element | undefined} html
 * @param {string | undefined} productId
 * @returns {HTMLElement | null}
 */
function getNewProductForm(html, productId) {
  if (!html?.querySelector) return null;

  if (productId) {
    const exactForm = html.querySelector(`${PRODUCT_FORM_SELECTOR}[data-product-id="${productId}"]`);
    if (exactForm instanceof HTMLElement) return exactForm;
  }

  const fallbackForm = html.querySelector(PRODUCT_FORM_SELECTOR);
  return fallbackForm instanceof HTMLElement ? fallbackForm : null;
}

/**
 * Block submission briefly while Shopify is fetching the newly selected variant.
 * This prevents the theme's rapid-click batch path from adding an item before
 * the private preorder property has been refreshed.
 * @param {HTMLElement} productForm
 */
function markVariantUpdatePending(productForm) {
  productForm.dataset[PENDING_ATTRIBUTE] = 'true';

  for (const button of productForm.querySelectorAll('[ref="addToCartButton"]')) {
    if (button instanceof HTMLButtonElement) button.disabled = true;
  }

  const acceleratedCheckout = productForm.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
  if (acceleratedCheckout instanceof HTMLElement) acceleratedCheckout.setAttribute('hidden', 'true');
}

/**
 * Synchronize the current hidden line-item property with Shopify's freshly
 * server-rendered form for the selected variant.
 * @param {HTMLElement} productForm
 * @param {HTMLElement | null} newProductForm
 */
function syncPreorderProperty(productForm, newProductForm) {
  const currentInput = productForm.querySelector(PREORDER_PROPERTY_SELECTOR);
  if (!(currentInput instanceof HTMLInputElement)) return;

  const newInput = newProductForm?.querySelector(PREORDER_PROPERTY_SELECTOR);

  // Fail closed: if the refreshed form has no enabled preorder marker,
  // don't submit _preorder on the current form.
  currentInput.disabled = !(newInput instanceof HTMLInputElement) || newInput.disabled;
  currentInput.value = 'true';
}

document.addEventListener('variant:selected', (event) => {
  const productForm = getProductFormForEvent(/** @type {Event & {detail?: any}} */ (event));
  if (!productForm) return;
  markVariantUpdatePending(productForm);
});

document.addEventListener('variant:update', (event) => {
  const variantEvent = /** @type {Event & {detail?: any}} */ (event);
  const productForm = getProductFormForEvent(variantEvent);
  if (!productForm) return;

  const productId = variantEvent.detail?.data?.newProduct?.id || variantEvent.detail?.data?.productId;
  const newProductForm = getNewProductForm(variantEvent.detail?.data?.html, productId);
  syncPreorderProperty(productForm, newProductForm);

  delete productForm.dataset[PENDING_ATTRIBUTE];
});

// Keyboard submission or an unusually fast synthetic submit can bypass a disabled
// button. Stop it while a variant change is unresolved rather than risk attaching
// a stale preorder state to the wrong variant.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const productForm = form.closest(PRODUCT_FORM_SELECTOR);
    if (!(productForm instanceof HTMLElement)) return;

    if (productForm.dataset[PENDING_ATTRIBUTE] === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);
